import BaseApiClient from '@api-client/BaseApiClient';
import { ListDataParams, ListDataResponse } from '@api-client/types';
import {
  EmailConnectionAuthorisation,
  EmailFolderNode,
  EmailFolderMessagesMeta,
  EmailInternetHeaders,
  EmailInterface,
  EmailMarkAsReadPayload,
  EmailSetProviderMessageReadPayload,
  EmailMessageDetails,
  EmailMessageLinkIds,
  LinkEmailMessagePayload,
  UnlinkEmailMessagePayload,
  EmailPossibleLinkCompany,
  EmailPossibleLinkIndividual,
  EmailPossibleLinks,
  EmailPossibleLinkTask,
  RecentEmailMessage,
  SendDirectEmailPayload,
  SendEmailInterface,
  TodaysCalendarEvent,
  UserEmailConnection
} from '@interfaces/Email.interface';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function toNullableNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function toStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  return value.map((entry) => String(entry));
}

export function normalizeEmailLinkStatus(raw: unknown): 'linked' | 'private' {
  const status = typeof raw === 'string' ? raw : '';
  if (status === 'linked' || status === 'shared') return 'linked';
  return 'private';
}

export function normalizeEmailLinks(raw: unknown): EmailMessageLinkIds {
  const links = isRecord(raw) ? raw : {};
  const individualIdsRaw = links.individual_ids;
  let individualIds: number[] = [];
  if (Array.isArray(individualIdsRaw)) {
    individualIds = individualIdsRaw
      .map((id) => toNullableNumber(id))
      .filter((id): id is number => id != null);
  } else {
    const legacyIndividualId = toNullableNumber(links.individual_id);
    if (legacyIndividualId != null) individualIds = [legacyIndividualId];
  }
  return {
    task_id: toNullableNumber(links.task_id),
    individual_ids: individualIds,
    company_id: toNullableNumber(links.company_id),
    email_thread_parent_id: toNullableNumber(links.email_thread_parent_id)
  };
}

function normalizePossibleLinkIndividual(
  raw: unknown
): EmailPossibleLinkIndividual {
  const item = isRecord(raw) ? raw : {};
  return {
    id: toNullableNumber(item.id) ?? 0,
    uuid: String(item.uuid ?? ''),
    name: String(item.name ?? ''),
    email: item.email == null ? null : String(item.email)
  };
}

function normalizePossibleLinkCompany(raw: unknown): EmailPossibleLinkCompany {
  const item = isRecord(raw) ? raw : {};
  return {
    id: toNullableNumber(item.id) ?? 0,
    uuid: String(item.uuid ?? ''),
    name: String(item.name ?? '')
  };
}

function normalizePossibleLinkTask(raw: unknown): EmailPossibleLinkTask {
  const item = isRecord(raw) ? raw : {};
  return {
    id: toNullableNumber(item.id) ?? 0,
    uuid: String(item.uuid ?? '')
  };
}

function normalizePossibleLinks(raw: unknown): EmailPossibleLinks | null {
  if (!isRecord(raw)) return null;
  return {
    individuals: Array.isArray(raw.individuals)
      ? raw.individuals.map(normalizePossibleLinkIndividual)
      : [],
    companies: Array.isArray(raw.companies)
      ? raw.companies.map(normalizePossibleLinkCompany)
      : [],
    tasks: Array.isArray(raw.tasks)
      ? raw.tasks.map(normalizePossibleLinkTask)
      : []
  };
}

function normalizeInternetHeaders(raw: unknown): EmailInternetHeaders {
  if (!isRecord(raw)) return {};
  return Object.fromEntries(
    Object.entries(raw).map(([key, value]) => [key, String(value ?? '')])
  );
}

export function normalizeMessageIsRead(raw: unknown): boolean {
  if (isRecord(raw) && raw.is_read === false) return false;
  return true;
}

function normalizeRecentEmailMessage(raw: unknown): RecentEmailMessage {
  const item = isRecord(raw) ? raw : {};
  return {
    provider_message_id: String(item.provider_message_id ?? ''),
    provider_thread_id:
      item.provider_thread_id == null ? null : String(item.provider_thread_id),
    internet_message_id:
      item.internet_message_id == null ? null : String(item.internet_message_id),
    subject: item.subject == null ? null : String(item.subject),
    body_preview: item.body_preview == null ? null : String(item.body_preview),
    from_address: item.from_address == null ? null : String(item.from_address),
    from_name: item.from_name == null ? null : String(item.from_name),
    to_address: item.to_address == null ? null : String(item.to_address),
    cc_addresses: toStringArray(item.cc_addresses),
    date: String(item.date ?? ''),
    has_attachments: Boolean(item.has_attachments),
    folder_id: item.folder_id == null ? null : String(item.folder_id),
    folder_name: item.folder_name == null ? null : String(item.folder_name),
    is_read: normalizeMessageIsRead(item),
    status: normalizeEmailLinkStatus(item.status),
    links: normalizeEmailLinks(item.links)
  };
}

function normalizeMessageDetails(raw: unknown): EmailMessageDetails {
  const item = isRecord(raw) ? raw : {};
  return {
    provider_message_id: String(item.provider_message_id ?? ''),
    provider_thread_id:
      item.provider_thread_id == null ? null : String(item.provider_thread_id),
    internet_message_id:
      item.internet_message_id == null ? null : String(item.internet_message_id),
    subject: item.subject == null ? null : String(item.subject),
    body_preview:
      item.body_preview == null ? null : String(item.body_preview),
    body_html: item.body_html == null ? null : String(item.body_html),
    body_text: item.body_text == null ? null : String(item.body_text),
    from_address: item.from_address == null ? null : String(item.from_address),
    from_name: item.from_name == null ? null : String(item.from_name),
    to_address: item.to_address == null ? null : String(item.to_address),
    cc_addresses: toStringArray(item.cc_addresses),
    date: String(item.date ?? ''),
    has_attachments: Boolean(item.has_attachments),
    folder_id: item.folder_id == null ? null : String(item.folder_id),
    folder_name: item.folder_name == null ? null : String(item.folder_name),
    is_read: normalizeMessageIsRead(item),
    internet_headers: normalizeInternetHeaders(item.internet_headers),
    status: normalizeEmailLinkStatus(item.status),
    links: normalizeEmailLinks(item.links),
    possible_links: normalizePossibleLinks(item.possible_links)
  };
}

function normalizeUserEmailConnection(
  raw: unknown
): UserEmailConnection | null {
  if (raw == null) return null;
  const item = isRecord(raw) ? raw : null;
  if (item == null) return null;
  return {
    id: toNullableNumber(item.id) ?? 0,
    email_address: String(item.email_address ?? ''),
    is_active: Boolean(item.is_active),
    connected_at:
      item.connected_at == null ? null : String(item.connected_at),
    last_synced_at:
      item.last_synced_at == null ? null : String(item.last_synced_at),
    has_webhook: Boolean(item.has_webhook)
  };
}

function normalizeEmailConnectionAuthorisation(
  raw: unknown
): EmailConnectionAuthorisation {
  const item = isRecord(raw) ? raw : {};
  if (item.auth_type === 'application') {
    return {
      auth_type: 'application',
      authorisation_url: null,
      connection: normalizeUserEmailConnection(item.connection) ?? {
        id: 0,
        email_address: '',
        is_active: false,
        connected_at: null,
        last_synced_at: null,
        has_webhook: false
      },
      message: String(item.message ?? '')
    };
  }
  return {
    auth_type: 'delegated',
    authorisation_url: String(item.authorisation_url ?? '')
  };
}

/** Coerce folder tree from API (snake or camel) so nested `children` is always a real array. */
function normalizeEmailFolderNode(raw: unknown): EmailFolderNode {
  if (!raw || typeof raw !== 'object') {
    return {
      id: '',
      name: '',
      parent_id: null,
      well_known_name: null,
      total_item_count: 0,
      unread_item_count: 0,
      children: []
    };
  }
  const r = raw as Record<string, unknown>;
  const childRaw =
    r.children ?? r.ChildFolders ?? r.child_folders ?? r.childFolders;
  const childArr = Array.isArray(childRaw) ? childRaw : [];
  return {
    id: String(r.id ?? ''),
    name: String(r.name ?? ''),
    parent_id: (r.parent_id ?? r.parentId ?? null) as string | null,
    well_known_name: (r.well_known_name ?? r.wellKnownName ?? null) as
      | string
      | null,
    total_item_count: Number(r.total_item_count ?? r.totalItemCount ?? 0),
    unread_item_count: Number(r.unread_item_count ?? r.unreadItemCount ?? 0),
    children: childArr.map(normalizeEmailFolderNode)
  };
}

export class ApiEmail extends BaseApiClient {
  async getConnection(): Promise<UserEmailConnection | null> {
    return new Promise((resolve, reject) => {
      this.client
        .get('email-connection')
        .then((res) => {
          if (res.data?.data === null) {
            resolve(null);
            return;
          }
          if (res.data != null) {
            resolve(normalizeUserEmailConnection(res.data) ?? null);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async authoriseConnection(): Promise<EmailConnectionAuthorisation> {
    return new Promise((resolve, reject) => {
      this.client
        .post('email-connection/authorise')
        .then((res) => {
          if (res.data != null) {
            resolve(normalizeEmailConnectionAuthorisation(res.data));
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async reconnectConnection(): Promise<EmailConnectionAuthorisation> {
    return new Promise((resolve, reject) => {
      this.client
        .post('email-connection/reconnect')
        .then((res) => {
          if (res.data != null) {
            resolve(normalizeEmailConnectionAuthorisation(res.data));
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async disconnectConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete('email-connection')
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }

  async recentMessages(): Promise<RecentEmailMessage[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('email-connection/recent-messages')
        .then((res) => {
          if (Array.isArray(res.data?.data)) {
            resolve(res.data.data.map(normalizeRecentEmailMessage));
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /** Microsoft Graph–backed folder tree (`GET /email/folders`). */
  async folders(): Promise<EmailFolderNode[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('email/folders')
        .then((res) => {
          if (res.data?.data != null) {
            const list = res.data.data as unknown[];
            resolve(list.map(normalizeEmailFolderNode));
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /** Paginated messages for a folder (`GET /email/folders/{folderId}/messages`). */
  async folderMessages(
    folderId: string,
    params: {
      page: number;
      perPage: number;
      sortBy?: 'date' | 'sender' | 'subject';
      direction?: 'asc' | 'desc';
      q?: string;
    }
  ): Promise<{ items: RecentEmailMessage[]; meta: EmailFolderMessagesMeta }> {
    const encodedFolderId = encodeURIComponent(folderId);
    const query: Record<string, string | number> = {
      page: params.page,
      per_page: params.perPage
    };
    const trimmedQ = params.q?.trim();
    if (trimmedQ) {
      query.q = trimmedQ;
    } else {
      query.sort_by = params.sortBy ?? 'date';
      query.direction = params.direction ?? 'desc';
    }

    return new Promise((resolve, reject) => {
      this.client
        .get(`email/folders/${encodedFolderId}/messages`, { params: query })
        .then((res) => {
          if (Array.isArray(res.data?.data) && res.data?.meta != null) {
            const meta = res.data.meta;
            resolve({
              items: res.data.data.map(normalizeRecentEmailMessage),
              meta: {
                page: meta.page,
                per_page: meta.per_page,
                total: meta.total ?? null,
                has_more: meta.has_more,
                sort_by: meta.sort_by,
                direction: meta.direction ?? null,
                search: meta.search ?? null
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async todaysEvents(timezone?: string): Promise<TodaysCalendarEvent[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('email-connection/todays-events', {
          params: timezone ? { timezone } : undefined
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getMessageDetails(
    providerMessageId: string
  ): Promise<EmailMessageDetails> {
    const encodedMessageId = encodeURIComponent(providerMessageId);
    return new Promise((resolve, reject) => {
      this.client
        .get(`email/messages/${encodedMessageId}`)
        .then((res) => {
          if (res.data?.data != null) {
            resolve(normalizeMessageDetails(res.data.data));
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async linkMessage(
    providerMessageId: string,
    payload: LinkEmailMessagePayload
  ): Promise<EmailMessageDetails> {
    const encodedMessageId = encodeURIComponent(providerMessageId);
    const body: Record<string, unknown> = {};
    if (payload.task_id != null) body.task_id = payload.task_id;
    // company_id is resolved server-side to the primary contact (CRM-549).
    if (payload.company_id != null) body.company_id = payload.company_id;
    if (payload.individual_ids != null && payload.individual_ids.length > 0) {
      body.individual_ids = payload.individual_ids;
    }
    return new Promise((resolve, reject) => {
      this.client
        .post(`email/messages/${encodedMessageId}/link`, body)
        .then((res) => {
          if (res.data?.data != null) {
            resolve(normalizeMessageDetails(res.data.data));
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async unlinkMessage(
    providerMessageId: string,
    payload: UnlinkEmailMessagePayload
  ): Promise<EmailMessageDetails> {
    const encodedMessageId = encodeURIComponent(providerMessageId);
    const body: Record<string, unknown> = {};
    if (payload.unlink_task === true) body.unlink_task = true;
    if (payload.individual_ids != null && payload.individual_ids.length > 0) {
      body.individual_ids = payload.individual_ids;
    }
    return new Promise((resolve, reject) => {
      this.client
        .post(`email/messages/${encodedMessageId}/unlink`, body)
        .then((res) => {
          if (res.data?.data != null) {
            resolve(normalizeMessageDetails(res.data.data));
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get list of items
   */
  async listEmails(
    listData: ListDataParams,
    taskId?: number
  ): Promise<ListDataResponse<EmailInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList(`task/${taskId}/email`, listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async listIndividualsEmails(
    listData: ListDataParams,
    individualId?: number
  ): Promise<ListDataResponse<EmailInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList(`/individual/${individualId}/email`, listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async listEmailsFromThread(threadId?: number): Promise<EmailInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`email/threads/${threadId}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async postNewThread(
    emailContent: EmailInterface
  ): Promise<SendEmailInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post('email/send', emailContent)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async sendDirect(payload: SendDirectEmailPayload): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.client
        .post('email/send', payload)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  async markAsRead(payload: EmailMarkAsReadPayload): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .post('email/mark-as-read', payload)
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }

  async setProviderMessageRead(
    providerMessageId: string,
    payload: EmailSetProviderMessageReadPayload
  ): Promise<void> {
    const encodedId = encodeURIComponent(providerMessageId);
    return new Promise((resolve, reject) => {
      this.client
        .patch(`email/messages/${encodedId}/read`, payload)
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }
}

export { emailQueryKeys } from './query-keys';
export type {
  EmailIndividualListQueryParams,
  EmailTaskListQueryParams
} from './query-keys';
export * from './use-email-queries';
export * from './use-email-mutations';
